import React, { useRef, useState, useEffect, useMemo } from 'react';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import { Spring } from 'react-spring/renderprops';
import RemoveButton from '../../components/RemoveButton';
import {ANIMATION_TIMEOUT_INOUT} from '../../constants/animation';
import {Role} from '../../helpers/user';
import FeedPostAvatar from './components/FeedPostAvatar';
import FeedPostCommentsBlock from './components/FeedPostCommentsBlock';
import FeedPostContent from './components/FeedPostContent';
import FeedPostSocial from './components/FeedPostSocial';
import { getPost } from './helper';
import './styles.css';

const FeedPost = props => {
  const { post, actions, user } = props;
  const {
    onRemoveComment,
    removePost,
    setLike,
    onAddComment,
    apply,
    checkApply,
    showPreviousComments,
    sendMessage,
    handleChangedHeroStatus,
    handleBeforeFirmRatingUpdate,
    handleFirmRatingUpdate,
    handleClickReviewLink,
    handleReviewRatingUpdate,
    handleBeforeReviewRatingUpdate,
    handleClickNewsLink,
    handleClickFirmPortfolioDeal,
    checkPermissions,
  } = actions;

  const {
    MoodID,
    TypeID,
    Likes,
    LikesCount,
    Comments,
    CommentCount,
    Removable,
  } = post;

  const commentsBlockTextAreaRef = useRef();

  const isCandidate = Role.isCandidate(user);

  const [options, setPostOptions] = useState({
    hideHeroProfile: !isCandidate,
    hideProcessLink: !isCandidate,
    isCandidate,
  });

  const [isRemoveButtonDisabled, setRemoveButtonDisabled] = useState(false);
  const [isCommentsBlockOpen, setCommentsBlockOpen] = useState(false);

  useEffect(() => {
    if (isCommentsBlockOpen) {
      commentsBlockTextAreaRef.current.focus();
    }
  }, [isCommentsBlockOpen]);

  const handleClickRemoveButton = () => {
    setRemoveButtonDisabled(true);
    removePost(MoodID).catch(() => setRemoveButtonDisabled(false));
  };

  const onClickStatusApply = () => {
    if (checkPermissions()) {
      checkApply(post).then(success =>
        success
          ? setPostOptions({ ...options, showDialog: true })
          : setPostOptions({ ...options, showDialog: false })
      );
    }
  };

  const onClickDialogButtonNo = () => {
    setPostOptions({ ...options, showDialog: false });
  };

  const onClickDialogButtonYes = () => {
    apply(post);
  };

  const onClickShowPreviousComments = () => {
    showPreviousComments(post);
  };

  const onLike = () => {
    setLike(post);
  };

  const onComment = () => {
    setCommentsBlockOpen(!isCommentsBlockOpen);
  };

  const renderedPost = useMemo(
    () =>
      getPost(
        post,
        {
          onClickDialogButtonNo,
          onClickDialogButtonYes,
          handleClickReviewLink,
          handleBeforeReviewRatingUpdate,
          handleReviewRatingUpdate,
          handleBeforeFirmRatingUpdate,
          handleFirmRatingUpdate,
          handleClickNewsLink,
          handleClickFirmPortfolioDeal,
          checkPermissions,
        },
        { options, user }
      ),
    [post, options, user]
  );

  const likAndCommentsBtns = Number(TypeID) !== 0 && (
    <div className="feed-post__social">
      <FeedPostSocial
        likes={Likes}
        likesCount={LikesCount}
        comments={Comments}
        commentsCount={CommentCount}
        onLike={onLike}
        onComment={onComment}
      />
    </div>
  );

  return (
    <Spring
      from={{ opacity: 0 }}
      to={{ opacity: 1 }}
      config={{ duration: ANIMATION_TIMEOUT_INOUT }}
    >
      {props => (
        <div style={props}>
          <article className="feed-post">
            {Removable && (
              <RemoveButton
                disabled={isRemoveButtonDisabled}
                controlFunc={handleClickRemoveButton}
                btnBg="transparent"
                customStyles={{
                  top: '2px',
                  right: '2px',
                  borderColor: '#f40',
                }}
              />
            )}
            <FeedPostAvatar
              user={user}
              post={post}
              actions={{
                onClickReviewLink: handleClickReviewLink,
                onClickApplyHandler: onClickStatusApply,
                onStatusChangeHandler: handleChangedHeroStatus,
                onSendMessageHandler: sendMessage,
              }}
            />

            <div className="feed-post__container">
              <div className="feed-post__content-wrapper">
                <MediaQuery query="(max-width: 767px)">
                  <FeedPostAvatar
                    isMobile
                    user={user}
                    post={post}
                    actions={{
                      onClickReviewLink: handleClickReviewLink,
                      onClickApplyHandler: onClickStatusApply,
                      onStatusChangeHandler: handleChangedHeroStatus,
                      onSendMessageHandler: sendMessage,
                    }}
                  />
                </MediaQuery>
                <FeedPostContent>{renderedPost}</FeedPostContent>
                {likAndCommentsBtns}
              </div>

              {isCommentsBlockOpen && (
                <FeedPostCommentsBlock
                  ref={commentsBlockTextAreaRef}
                  user={user}
                  post={post}
                  actions={{
                    onAddComment,
                    onRemoveComment,
                    onClickShowPreviousComments,
                  }}
                />
              )}
            </div>
          </article>
        </div>
      )}
    </Spring>
  );
};

FeedPost.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  post: PropTypes.object.isRequired,
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    avatar: PropTypes.oneOfType([
      PropTypes.shape({
        original: PropTypes.string.isRequired,
        s155x155: PropTypes.string.isRequired,
        s65x65: PropTypes.string.isRequired,
        s55x55: PropTypes.string.isRequired,
        s38x38: PropTypes.string.isRequired,
      }).isRequired,
      PropTypes.string.isRequired,
    ]),
  }).isRequired,
  HeroLink: PropTypes.string,
  date: PropTypes.string,
};

export default FeedPost;
